/* Generic data table + crud helpers — with auto-branch attach + CSV export. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  Search,
  Loader2,
  Download,
  Printer,
  CreditCard,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { downloadCSV } from "@/lib/csv";
import { DatePicker } from "@/components/common/DatePicker";
import ModernLoader from "@/components/common/ModernLoader";
import {
  clearRolePermissionsCache,
  loadRolePermissions,
  normalizePermissionKey,
  resolvePermissionSet,
  findRolePermissionEntry,
} from "@/lib/permissions";

function resolveModuleKeyFromPath(pathname) {
  const segment = String(pathname || "")
    .split("/")
    .filter(Boolean)[0];

  return normalizePermissionKey(segment || "dashboard");
}

export function useRouteModulePermissions(
  overrideModuleKey,
  overrideSubmoduleKey,
) {
  const location = useLocation();
  const { user, isSuperAdmin } = useAuth();
  const [rolePermissions, setRolePermissions] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (isSuperAdmin) {
        if (!cancelled) {
          setRolePermissions({});
        }
        return;
      }

      try {
        const data = await loadRolePermissions(api);
        if (!cancelled) {
          setRolePermissions(data || {});
        }
      } catch {
        if (!cancelled) {
          setRolePermissions({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, user?.role]);

  const moduleKey = useMemo(
    () =>
      normalizePermissionKey(
        overrideModuleKey || resolveModuleKeyFromPath(location.pathname),
      ),
    [location.pathname, overrideModuleKey],
  );

  const submoduleKey = useMemo(
    () => normalizePermissionKey(overrideSubmoduleKey || ""),
    [overrideSubmoduleKey],
  );

  const permissions = useMemo(() => {
    const roleEntry = findRolePermissionEntry(rolePermissions, user?.role);
    return resolvePermissionSet(
      roleEntry,
      moduleKey,
      submoduleKey || null,
      isSuperAdmin,
    );
  }, [rolePermissions, user?.role, moduleKey, submoduleKey, isSuperAdmin]);

  return {
    moduleKey,
    submoduleKey,
    canView: permissions.view,
    canAdd: permissions.add,
    canEdit: permissions.edit,
    canDelete: permissions.delete,
  };
}

export function PageTitle({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
      <div>
        <h1 className="text-xl md:text-2xl font-bold tracking-tight font-serif-display erp-text-primary">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

export function DataTable({
  columns,
  rows,
  onEdit,
  onDelete,
  onPrint,
  onIdCard,
  emptyLabel = "No records yet",
  testid = "data-table",
}) {
  return (
    <div
      className="border rounded-md overflow-hidden bg-white shadow-sm"
      style={{ borderColor: "var(--erp-border)" }}
    >
      <div className="overflow-x-auto">
        <table
          className="w-full text-left border-collapse min-w-[640px]"
          data-testid={testid}
        >
          <thead className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wide">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-3 py-2 font-semibold border-b border-slate-200"
                >
                  {c.label}
                </th>
              ))}
              {(onEdit || onDelete || onPrint || onIdCard) && (
                <th className="px-3 py-2 font-semibold border-b border-slate-200 w-32 text-right">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (onEdit || onDelete || onPrint || onIdCard ? 1 : 0)
                  }
                  className="px-3 py-12 text-center text-slate-400 text-xs"
                >
                  <div className="font-medium mb-1">{emptyLabel}</div>
                  <div className="text-[11px] text-slate-300">
                    Click "Add New" to create the first one
                  </div>
                </td>
              </tr>
            )}
            {rows.map((row, i) => (
              <tr
                key={row.id || i}
                className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className="px-3 py-2 align-middle text-slate-800"
                  >
                    {c.render
                      ? c.render(row, i)
                      : (row[c.key] ?? (
                          <span className="text-slate-300">—</span>
                        ))}
                  </td>
                ))}
                {(onEdit || onDelete || onPrint || onIdCard) && (
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {onPrint && (
                        <button
                          data-testid={`print-${row.id}`}
                          className="p-1.5 rounded hover:bg-amber-50 text-amber-700"
                          onClick={() => onPrint(row)}
                          title="Print"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onIdCard && (
                        <button
                          data-testid={`idcard-${row.id}`}
                          className="p-1.5 rounded hover:bg-teal-50 text-teal-700"
                          onClick={() => onIdCard(row)}
                          title="ID Card"
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onEdit && (
                        <button
                          data-testid={`edit-${row.id}`}
                          className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                          onClick={() => onEdit(row)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          data-testid={`delete-${row.id}`}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          onClick={() => onDelete(row)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Generic master CRUD page — auto-attaches branch_id; supports CSV export. */
export function MasterCrudPage({
  title,
  endpoint,
  fields,
  columns,
  testid = "master-page",
  scoped = false,
  dateField,
  onPrint,
  moduleKey,
  submoduleKey,
}) {
  const { branchId, sessionId } = useAuth();
  const permissions = useRouteModulePermissions(moduleKey, submoduleKey);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v));
    if (typeof value === "string") {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
    if (value === null || value === undefined || value === "") return [];
    return [String(value)];
  };

  const sortOptionsByLabel = useCallback((options, sortMode) => {
    if (!Array.isArray(options)) return [];
    if (sortMode === false || sortMode === "none") return options;

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    const sorted = [...options].sort((a, b) =>
      collator.compare(String(a?.label ?? ""), String(b?.label ?? "")),
    );

    return sortMode === "desc" ? sorted.reverse() : sorted;
  }, []);

  const parseIdList = useCallback((value) => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v).trim()).filter(Boolean);
        }
      } catch {
        // fallback to CSV below
      }
      return trimmed
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
    if (value === null || value === undefined) return [];
    return [String(value).trim()].filter(Boolean);
  }, []);

  const getFilteredFieldOptions = useCallback(
    (field, sourceOptions) => {
      let options = [...(sourceOptions || [])];

      if (field.allowedIdsFromSelected?.field) {
        const parentFieldKey = field.allowedIdsFromSelected.field;
        const sourceKey = field.allowedIdsFromSelected.sourceKey || "subjects";
        const parentValue = String(form[parentFieldKey] ?? "");

        if (parentValue) {
          const parentOptions =
            (dynamicOptions[parentFieldKey] &&
            dynamicOptions[parentFieldKey].length
              ? dynamicOptions[parentFieldKey]
              : fields.find((f) => f.key === parentFieldKey)?.options || []) ||
            [];

          const selectedParent = parentOptions.find(
            (o) => String(o.value) === parentValue,
          );
          const allowedIds = parseIdList(selectedParent?.raw?.[sourceKey]);
          if (allowedIds.length > 0) {
            const allowed = new Set(allowedIds.map((id) => String(id)));
            options = options.filter((o) => allowed.has(String(o.value)));
          }
        }
      }

      if (field.clientFilterMap && typeof field.clientFilterMap === "object") {
        const unfiltered = [...options];
        const entries = Object.entries(field.clientFilterMap);

        for (const [formKey, optionKey] of entries) {
          const formValue = form[formKey];
          const required =
            Array.isArray(field.requireFilters) &&
            field.requireFilters.includes(formKey);

          if (
            formValue === undefined ||
            formValue === null ||
            formValue === ""
          ) {
            if (required) return [];
            continue;
          }

          options = options.filter(
            (o) => String(o?.raw?.[optionKey] ?? "") === String(formValue),
          );
        }

        if (options.length === 0 && field.showAllIfFilteredEmpty) {
          options = unfiltered;
        }
      }

      return options;
    },
    [dynamicOptions, fields, form, parseIdList],
  );

  const prepareFormForEdit = useCallback(
    (row) => {
      const next = { ...row };
      fields.forEach((f) => {
        if (f.type === "multiselect") {
          next[f.key] = normalizeMultiValue(row?.[f.key]);
        }
      });
      return next;
    },
    [fields],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 280);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: debouncedSearch || undefined, limit: 500 };
      if (scoped && branchId) params.branch_id = branchId;
      if (scoped && sessionId) params.session_id = sessionId;
      const { data } = await api.get(endpoint, { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error("Failed to load");
    }
    setLoading(false);
  }, [endpoint, debouncedSearch, branchId, sessionId, scoped]);

  useEffect(() => {
    load();
  }, [load]);

  // Apply date range filter client-side when dateField provided
  const filteredRows = (() => {
    if (!dateField) return rows;
    return rows.filter((r) => {
      const v = (r[dateField] || "").slice(0, 10);
      if (dateFrom && v < dateFrom) return false;
      if (dateTo && v > dateTo) return false;
      return true;
    });
  })();

  const openCreate = () => {
    setEditing(null);
    const next = {};
    fields.forEach((f) => {
      if (f.type === "multiselect") next[f.key] = [];
    });
    setForm(next);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm(prepareFormForEdit(row));
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadFieldOptions = async () => {
      const nextOptions = {};

      for (const f of fields) {
        if (!f.optionsEndpoint) continue;

        const params = { limit: 1000 };
        if (scoped && branchId) params.branch_id = branchId;
        if (scoped && sessionId) params.session_id = sessionId;

        if (f.dependsOn) {
          const depends = Array.isArray(f.dependsOn)
            ? f.dependsOn
            : [f.dependsOn];
          let hasMissingDependency = false;

          depends.forEach((depKey) => {
            const depValue = form[depKey];
            if (
              depValue === undefined ||
              depValue === null ||
              depValue === "" ||
              (Array.isArray(depValue) && depValue.length === 0)
            ) {
              hasMissingDependency = true;
              return;
            }

            const paramKey =
              (f.dependsParamMap && f.dependsParamMap[depKey]) ||
              (depends.length === 1 ? f.dependsParam || depKey : depKey);

            params[paramKey] = Array.isArray(depValue)
              ? depValue.join(",")
              : depValue;
          });

          if (hasMissingDependency) {
            nextOptions[f.key] = [];
            continue;
          }
        }

        if (Array.isArray(f.extraParamsFromForm)) {
          f.extraParamsFromForm.forEach((paramKey) => {
            const paramValue = form[paramKey];
            if (
              paramValue === undefined ||
              paramValue === null ||
              paramValue === "" ||
              (Array.isArray(paramValue) && paramValue.length === 0)
            ) {
              return;
            }
            params[paramKey] = Array.isArray(paramValue)
              ? paramValue.join(",")
              : paramValue;
          });
        }

        try {
          const fetchMappedOptions = async (effectiveParams) => {
            const { data } = await api.get(f.optionsEndpoint, {
              params: effectiveParams,
            });
            const items = data?.items || [];
            const valueKey = f.optionValueKey || "id";
            const labelKey = f.optionLabelKey || "name";
            return items.map((item) => ({
              value: String(item[valueKey] ?? ""),
              label: String(item[labelKey] ?? item[valueKey] ?? ""),
              raw: item,
            }));
          };

          let mapped = await fetchMappedOptions(params);

          // Fallback support: if strict filters produce empty result,
          // drop optional params step-by-step and retry.
          if (mapped.length === 0 && Array.isArray(f.fallbackDropParams)) {
            const fallbackParams = { ...params };
            for (const dropKey of f.fallbackDropParams) {
              if (!(dropKey in fallbackParams)) continue;
              delete fallbackParams[dropKey];
              mapped = await fetchMappedOptions(fallbackParams);
              if (mapped.length > 0) break;
            }
          }

          nextOptions[f.key] = sortOptionsByLabel(
            mapped,
            f.sortOptions || "asc",
          );
        } catch {
          nextOptions[f.key] = [];
        }
      }

      if (!cancelled) {
        setDynamicOptions((prev) => ({ ...prev, ...nextOptions }));
      }
    };

    loadFieldOptions();

    return () => {
      cancelled = true;
    };
  }, [open, fields, form, scoped, branchId, sessionId, sortOptionsByLabel]);

  const save = async () => {
    if (saving) return;
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for this module");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for this module");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };

      fields.forEach((f) => {
        if (f.type === "multiselect") {
          payload[f.key] = normalizeMultiValue(payload[f.key]).join(",");
        }
      });

      const normalize = (v) =>
        String(v ?? "")
          .trim()
          .toLowerCase();
      const explicitUniqueKeys = fields
        .filter((f) => f.unique)
        .map((f) => f.key);
      const inferredUniqueKeys = explicitUniqueKeys.length
        ? explicitUniqueKeys
        : fields.some((f) => f.key === "name")
          ? ["name"]
          : fields.some((f) => f.key === "title")
            ? ["title"]
            : [];

      if (inferredUniqueKeys.length) {
        const payloadHasUniqueValue = inferredUniqueKeys.some(
          (k) => normalize(payload[k]) !== "",
        );

        if (payloadHasUniqueValue) {
          const duplicate = rows.find((row) => {
            if (editing && String(row.id) === String(editing.id)) return false;
            return inferredUniqueKeys.every(
              (k) => normalize(row[k]) === normalize(payload[k]),
            );
          });

          if (duplicate) {
            toast.error("Duplicate entry not allowed");
            return;
          }
        }
      }

      // Auto-attach branch_id and session_id from logged-in context (unless explicitly cleared by SuperAdmin)
      if (!payload.branch_id && branchId) payload.branch_id = branchId;
      if (scoped && !payload.session_id && sessionId)
        payload.session_id = sessionId;
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated successfully");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created successfully");
      }
      if (endpoint === "/sessions") {
        window.dispatchEvent(new CustomEvent("erp-sessions-updated"));
      }
      clearRolePermissionsCache();
      setOpen(false);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => setDeleteRow(row);

  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for this module");
      return;
    }
    try {
      await api.delete(`${endpoint}/${deleteRow.id}`);
      toast.success("Deleted");
      if (endpoint === "/sessions") {
        window.dispatchEvent(new CustomEvent("erp-sessions-updated"));
      }
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const cols = columns || fields.map((f) => ({ key: f.key, label: f.label }));

  const exportCSV = () => {
    if (filteredRows.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    downloadCSV(
      filteredRows,
      cols,
      `${title.replace(/\s+/g, "_").toLowerCase()}.csv`,
    );
    toast.success(`Exported ${filteredRows.length} rows`);
  };

  return (
    <div data-testid={testid}>
      <PageTitle
        title={title}
        actions={
          <>
            {dateField && (
              <div className="flex items-end gap-2 mr-1 border-r border-slate-200 pr-3 self-end">
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                    From
                  </Label>
                  <DatePicker
                    value={dateFrom}
                    onChange={setDateFrom}
                    className="h-9 w-36 text-xs"
                    testId={`${testid}-from`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                    To
                  </Label>
                  <DatePicker
                    value={dateTo}
                    onChange={setDateTo}
                    className="h-9 w-36 text-xs"
                    testId={`${testid}-to`}
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                    }}
                    className="h-9 text-xs px-2"
                    data-testid={`${testid}-date-clear`}
                  >
                    Clear
                  </Button>
                )}
              </div>
            )}
            <div className="relative self-end">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 w-44 md:w-60 text-sm"
                data-testid={`${testid}-search`}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exportCSV}
              className="h-9 self-end"
              data-testid={`${testid}-export`}
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            {onPrint && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPrint(filteredRows)}
                className="h-9 self-end"
                data-testid={`${testid}-print`}
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> Print
              </Button>
            )}
            {permissions.canAdd && (
              <Button
                size="sm"
                onClick={openCreate}
                data-testid={`${testid}-create`}
                className="h-9 self-end erp-primary"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add New
              </Button>
            )}
          </>
        }
      />

      {loading ? (
        <ModernLoader
          compact
          title={`Loading ${title}`}
          subtitle="Fetching records"
        />
      ) : (
        <DataTable
          columns={cols}
          rows={filteredRows}
          onEdit={permissions.canEdit ? openEdit : undefined}
          onDelete={permissions.canDelete ? openDelete : undefined}
          testid={`${testid}-table`}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {editing ? "Edit" : "Add New"} · {title}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                {f.type === "select" ? (
                  (() => {
                    const options = getFilteredFieldOptions(
                      f,
                      (dynamicOptions[f.key] && dynamicOptions[f.key].length
                        ? dynamicOptions[f.key]
                        : f.options) || [],
                    );
                    return (
                      <select
                        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                        value={form[f.key] ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          const nextForm = { ...form, [f.key]: value };
                          if (Array.isArray(f.resetOnChange)) {
                            f.resetOnChange.forEach((resetKey) => {
                              const resetField = fields.find(
                                (rf) => rf.key === resetKey,
                              );
                              nextForm[resetKey] =
                                resetField?.type === "multiselect" ? [] : "";
                            });
                          }
                          setForm(nextForm);
                        }}
                        data-testid={`field-${f.key}`}
                      >
                        <option value="">— Select —</option>
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    );
                  })()
                ) : f.type === "multiselect" ? (
                  (() => {
                    const baseOptions = getFilteredFieldOptions(
                      f,
                      (dynamicOptions[f.key] && dynamicOptions[f.key].length
                        ? dynamicOptions[f.key]
                        : f.options) || [],
                    );
                    const options = sortOptionsByLabel(
                      baseOptions,
                      f.sortOptions || "asc",
                    );
                    const currentValue = normalizeMultiValue(form[f.key]);
                    const missingRequiredKey = (
                      f.requireFilters || []
                    ).find((key) => !form[key]);
                    const missingRequiredLabel = fields.find(
                      (rf) => rf.key === missingRequiredKey,
                    )?.label;
                    return (
                      <div
                        className="min-h-24 max-h-32 overflow-y-auto w-full rounded-md border border-slate-300 text-sm px-2 py-1.5 bg-white space-y-1"
                        data-testid={`field-${f.key}`}
                      >
                        {options.length === 0 && missingRequiredLabel && (
                          <div className="text-xs text-slate-400 py-1">
                            Select {missingRequiredLabel.replace(/\s*\*$/, "")}{" "}
                            first
                          </div>
                        )}
                        {options.length === 0 && !missingRequiredLabel && (
                          <div className="text-xs text-slate-400 py-1">
                            No {f.label?.replace(/\s*\(.*\)$/, "") || "options"}{" "}
                            available for this selection
                          </div>
                        )}
                        {options.map((o) => {
                          const checked = currentValue.includes(
                            String(o.value),
                          );
                          return (
                            <label
                              key={o.value}
                              className="flex items-center gap-2 text-sm text-slate-700"
                            >
                              <input
                                type="checkbox"
                                className="rounded border-slate-300"
                                checked={checked}
                                onChange={(e) => {
                                  const nextSet = new Set(currentValue);
                                  if (e.target.checked) {
                                    nextSet.add(String(o.value));
                                  } else {
                                    nextSet.delete(String(o.value));
                                  }
                                  setForm({
                                    ...form,
                                    [f.key]: Array.from(nextSet),
                                  });
                                }}
                              />
                              <span>{o.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : f.type === "textarea" ? (
                  <textarea
                    rows={3}
                    className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                    value={form[f.key] ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, [f.key]: e.target.value })
                    }
                    data-testid={`field-${f.key}`}
                  />
                ) : f.type === "date" ? (
                  <DatePicker
                    value={form[f.key] ?? ""}
                    onChange={(v) => setForm({ ...form, [f.key]: v })}
                    className="h-9"
                    testId={`field-${f.key}`}
                  />
                ) : (
                  <Input
                    type={f.type || "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        [f.key]:
                          f.type === "number"
                            ? e.target.value === ""
                              ? ""
                              : Number(e.target.value)
                            : e.target.value,
                      })
                    }
                    className="h-9"
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="erp-primary"
              data-testid="dialog-save"
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete {deleteRow?.name || deleteRow?.title || deleteRow?.id}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This action cannot be undone and the selected record will be
              permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
